/**
 * galeria3/photo-match-klient.jsx — „Podmień na oryginały": Photo Match PRZEZ PRZEGLĄDARKĘ
 * dla klienta chmury (2026-09-07, plan `SAAS/PLAN_photo_match_przegladarka.md`).
 *
 * Operatorski Photo Match (`PhotoMatchModal` w app.jsx, GOLD) pyta folder oryginałów na DYSKU
 * SERWERA — u klienta go nie ma. Tu oryginały są w przeglądarce (folder z telefonu), a serwer
 * dostaje kolejno: miniaturki (→ odciski), odciski (→ dopasowanie), TYLKO dopasowane zdjęcia,
 * zmniejszone (→ podmiana przez magazyn). Trzy trasy `/api/photo-match/przegladarka/*`.
 *
 * Etapy: pick → hashing → matching → results → replacing → done | error.
 * Pamięć odcisków w IndexedDB: drugie uruchomienie na tym samym folderze liczy tylko nowe pliki.
 *
 * ⚠️ Modal montuje `App` w app.jsx przy drodze przeglądarkowej (`drogaPhotoMatch` w widok-klienta.js):
 *    widok klienta ORAZ właściciel w chmurze (O137, 2026-10-20). Operator na Macu ma swój (dysk serwera).
 * Przeglądarka NIE trzyma plików między odświeżeniami — klient zawsze wskazuje folder ponownie
 * (to jedno kliknięcie; odciski są w pamięci, więc trwa sekundy, nie minuty).
 */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  jestJpg, miniaturka, zmniejsz, exifDataCzas, poKolei,
  kluczPliku, odciskiZPamieci, zapamietajOdciski,
} from './obraz-w-przegladarce.js';
import { przygotujCenePodmiany, wyslijPodmiany } from './photo-match-cena.js';
import { useUkladEkranu } from './uklad-ekranu.jsx';

const MINIATUREK_NA_ZADANIE = 100;   // serwer przyjmuje 200; 100 = ~1,2 MB na żądanie, częstszy postęp
const NARAZ_DEKODOWANIE = 3;         // ile zdjęć przeglądarka dekoduje jednocześnie (telefon!)
const NARAZ_WYSYLKA = 2;

async function czytajBlad(resp, domyslny) {
  const raw = await resp.text().catch(() => '');
  try {
    const j = JSON.parse(raw);
    const d = j && j.detail;
    if (typeof d === 'string' && d.trim()) return `${d} (HTTP ${resp.status})`;
    if (Array.isArray(d)) return `${d.map(x => x && x.msg).filter(Boolean).join(' • ') || domyslny} (HTTP ${resp.status})`;
  } catch { /* nie-JSON */ }
  return `${domyslny} (HTTP ${resp.status})`;
}

const btn = (busy, primary = true) => ({
  background: busy ? 'var(--panel-2)' : (primary ? 'var(--accent)' : 'transparent'),
  color: busy ? 'var(--text-3)' : (primary ? 'var(--on-accent)' : 'var(--text-2)'),
  border: primary ? 'none' : '1px solid var(--line-2)',
  padding: '8px 16px', borderRadius: 7, fontSize: 14, fontWeight: 600,
  cursor: busy ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
  fontFamily: 'inherit',
});

function Pasek({ teraz, razem, tekst }) {
  const pct = razem > 0 ? Math.round(100 * teraz / razem) : 0;
  return (
    <div style={{ margin: '14px 0' }}>
      <div style={{ fontSize: 12.5, color: 'var(--text-2)', marginBottom: 6 }}>{tekst} {razem > 0 && `${teraz} / ${razem}`}</div>
      <div style={{ height: 6, background: 'var(--panel-2)', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: 'var(--accent)', transition: 'width 0.2s' }} />
      </div>
    </div>
  );
}

export function PhotoMatchKlientModal({ selectedPhotoRefs, onClose, fire, onReplaceComplete }) {
  // Telefon (2026-09-30, plan SAAS/PLAN_studio_telefon_tablet.md §4 1a — zmierzone przy 390 px: wiersz wyniku
  // 420–451 px w oknie 297–355 px, „podmień” poza ekranem): okno na cały ekran, wiersze się zawijają,
  // pole wyboru i przyciski jako cele ≥ 44 px pod palcem. Komputer bez zmian.
  const { telefon, dotyk } = useUkladEkranu();
  const cel = dotyk ? { minHeight: 44 } : null;
  const sourceIds = useMemo(() => Array.from(new Set(selectedPhotoRefs.map(r => r.source_id))).sort(), [selectedPhotoRefs]);
  const [stage, setStage] = useState('pick');
  const [postep, setPostep] = useState({ teraz: 0, razem: 0, tekst: '' });
  const [wynik, setWynik] = useState(null);           // odpowiedź /dopasuj
  const [decyzje, setDecyzje] = useState({});         // `${sid}__${webp}` → true/false
  const [podmiany, setPodmiany] = useState([]);       // wyniki /podmien
  const [blad, setBlad] = useState(null);
  const [pominiete, setPominiete] = useState({ nieJpg: 0, nieczytelne: 0 });
  const plikiRef = useRef(new Map());                 // ref → File
  const urlRef = useRef(new Map());                   // ref → objectURL (miniatury w wynikach)
  const inputRef = useRef(null);

  const urlOryginalu = useCallback((ref) => {
    if (!ref) return null;
    if (!urlRef.current.has(ref)) {
      const f = plikiRef.current.get(ref);
      if (!f) return null;
      urlRef.current.set(ref, URL.createObjectURL(f));
    }
    return urlRef.current.get(ref);
  }, []);

  const zwolnijUrle = useCallback(() => {
    for (const u of urlRef.current.values()) URL.revokeObjectURL(u);
    urlRef.current.clear();
  }, []);
  const zamknij = useCallback(() => { zwolnijUrle(); onClose(); }, [onClose, zwolnijUrle]);
  const naBlad = useCallback((e, domyslny) => {
    setBlad(String((e && e.message) || e || domyslny));
    setStage('error');
  }, []);

  /* ── 1. folder → miniaturki → odciski ─────────────────────────────────────── */
  const naFolder = useCallback(async (ev) => {
    const wszystkie = Array.from(ev.target.files || []);
    if (wszystkie.length === 0) return;
    zwolnijUrle();                       // miniatury z POPRZEDNIEGO folderu nie mogą pokazać się przy nowym
    try {
      await naFolderWnetrze(wszystkie);
    } catch (e) {
      naBlad(e, 'Nie udało się przejrzeć folderu');   // sieć / dekoder — modal NIE zostaje zamrożony
    }
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const naFolderWnetrze = useCallback(async (wszystkie) => {
    const jpg = wszystkie.filter(jestJpg);
    setPominiete({ nieJpg: wszystkie.length - jpg.length, nieczytelne: 0 });
    if (jpg.length === 0) { setBlad('W tym folderze nie ma zdjęć JPG. Zdjęcia z iPhone\'a w HEIC trzeba najpierw zapisać jako JPG.'); setStage('error'); return; }
    plikiRef.current = new Map();
    for (const f of jpg) plikiRef.current.set(f.webkitRelativePath || f.name, f);

    setStage('hashing');
    setPostep({ teraz: 0, razem: jpg.length, tekst: 'Przeglądam zdjęcia…' });
    const klucze = jpg.map(kluczPliku);
    const zPamieci = await odciskiZPamieci(klucze);
    const oryginaly = [];
    const doPoliczenia = [];
    jpg.forEach((f, i) => {
      const w = zPamieci.get(klucze[i]);
      if (w && w.phash) oryginaly.push({ ref: f.webkitRelativePath || f.name, phash: w.phash, dhash: w.dhash, exif_dt: w.exif_dt, width: w.width, height: w.height });
      else doPoliczenia.push(f);
    });
    let gotowe = jpg.length - doPoliczenia.length;
    setPostep({ teraz: gotowe, razem: jpg.length, tekst: 'Przeglądam zdjęcia…' });

    let nieczytelne = 0;
    // partiami: dekoduj ≤3 naraz, wyślij po 100 miniaturek, zapamiętaj odciski
    for (let start = 0; start < doPoliczenia.length; start += MINIATUREK_NA_ZADANIE) {
      const partia = doPoliczenia.slice(start, start + MINIATUREK_NA_ZADANIE);
      const przygotowane = await poKolei(partia, NARAZ_DEKODOWANIE, async (f) => {
        const [m, exif] = await Promise.all([miniaturka(f, 256), exifDataCzas(f)]);
        return { f, ref: f.webkitRelativePath || f.name, blob: m.blob, width: m.width, height: m.height, exif_dt: exif };
      });
      const fd = new FormData();
      const meta = [];
      for (const p of przygotowane) {
        if (!p.ok) { nieczytelne++; continue; }
        fd.append('ref', p.wynik.ref);
        fd.append('miniaturka', p.wynik.blob, 'm.jpg');
        meta.push(p.wynik);
      }
      if (meta.length > 0) {
        const r = await fetch('/api/photo-match/przegladarka/odciski', { method: 'POST', body: fd });
        if (!r.ok) { setBlad(await czytajBlad(r, 'Serwer nie policzył odcisków')); setStage('error'); return; }
        const dane = await r.json();
        const poRef = new Map((dane.odciski || []).map(o => [o.ref, o]));
        const doZapamietania = [];
        for (const m of meta) {
          const o = poRef.get(m.ref);
          if (!o || !o.phash) { nieczytelne++; continue; }
          const wpis = { klucz: kluczPliku(m.f), phash: o.phash, dhash: o.dhash, exif_dt: m.exif_dt, width: m.width, height: m.height };
          doZapamietania.push(wpis);
          oryginaly.push({ ref: m.ref, phash: o.phash, dhash: o.dhash, exif_dt: m.exif_dt, width: m.width, height: m.height });
        }
        await zapamietajOdciski(doZapamietania);
      }
      gotowe += partia.length;
      setPostep({ teraz: gotowe, razem: jpg.length, tekst: 'Przeglądam zdjęcia…' });
    }
    setPominiete(p => ({ ...p, nieczytelne }));
    await dopasuj(oryginaly);
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  /* ── 2. dopasowanie na serwerze ───────────────────────────────────────────── */
  const dopasuj = useCallback(async (oryginaly) => {
    setStage('matching');
    setPostep({ teraz: 0, razem: 0, tekst: 'Dopasowuję…' });
    const r = await fetch('/api/photo-match/przegladarka/dopasuj', {   // wyjątek sieci łapie `naFolder`
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ source_ids: sourceIds, oryginaly }),
    });
    if (!r.ok) { setBlad(await czytajBlad(r, 'Dopasowanie nie powiodło się')); setStage('error'); return; }
    const dane = await r.json();
    const d = {};
    for (const g of (dane.groups || [])) {
      for (const m of (g.matches || [])) {
        const edyt = (g.edits || {})[m.webp_filename];
        const maEdycje = !!(edyt && (edyt.bg || edyt.shadow || edyt.vinted_dedupe));
        d[`${g.source_id}__${m.webp_filename}`] = m.confidence === 'high' && !maEdycje;   // pewne = zaznaczone, chyba że ma nałożone tło
      }
      for (const p of (g.sequence_proposals || [])) d[`${g.source_id}__${p.webp_filename}`] = false;
    }
    setWynik(dane); setDecyzje(d); setStage('results');
  }, [sourceIds]);

  /* ── 3. podmiana — jedno zdjęcie = jedno żądanie ──────────────────────────── */
  const doPodmiany = useMemo(() => {
    if (!wynik) return [];
    const out = [];
    for (const g of (wynik.groups || [])) {
      for (const m of (g.matches || [])) {
        if (m.matched && decyzje[`${g.source_id}__${m.webp_filename}`]) out.push({ sid: g.source_id, webp: m.webp_filename, ref: m.org_path });
      }
      for (const p of (g.sequence_proposals || [])) {
        if (decyzje[`${g.source_id}__${p.webp_filename}`]) out.push({ sid: g.source_id, webp: p.webp_filename, ref: p.suggested_org_path });
      }
    }
    return out;
  }, [wynik, decyzje]);

  const trwaPodmianaRef = useRef(false);
  const podmien = useCallback(async () => {
    if (doPodmiany.length === 0) return;
    // Blokada w locie: stan `replacing` chowa przycisk dopiero po przerysowaniu, a dwa kliknięcia w jednej
    // klatce wyceniłyby i wysłały partię dwa razy (panel na diffie ekranu 6, Muse).
    if (trwaPodmianaRef.current) return;
    trwaPodmianaRef.current = true;
    try { await podmienWnetrze(); } catch (e) { naBlad(e, 'Podmiana przerwana'); } finally { trwaPodmianaRef.current = false; }
  }, [doPodmiany, onReplaceComplete, fire]);  // eslint-disable-line react-hooks/exhaustive-deps

  const podmienWnetrze = useCallback(async () => {
    const n = doPodmiany.length;
    const powiedz = (tekst, rodzaj) => { if (fire) fire(tekst, rodzaj); else window.alert(tekst); };
    // CASE-105 (a) ekran 6: kwota PRZED wysyłką i w nagłówku każdej sztuki (`photo-match-cena.js`).
    // Stan `replacing` od razu — przycisk znika, więc podwójne kliknięcie nie wyceni i nie wyśle dwa razy.
    setStage('replacing');
    setPostep({ teraz: 0, razem: n, tekst: 'Sprawdzam cenę…' });
    const cena = await przygotujCenePodmiany(window.LF_ZAM, n, (tekst) => window.confirm(tekst));
    if (!cena.start) {
      setStage('results');
      if (!cena.anulowano) powiedz(cena.powod, 'danger');
      return;
    }
    setPostep({ teraz: 0, razem: n, tekst: 'Wgrywam oryginały…' });
    const wyniki = await wyslijPodmiany(doPodmiany, cena.kwoty, {
      przygotuj: async (el) => {
        const f = plikiRef.current.get(el.ref);
        if (!f) throw new Error('Nie znajduję pliku w wybranym folderze');
        const z = await zmniejsz(f, 2000, 0.9);
        const fd = new FormData();
        fd.append('source_id', el.sid); fd.append('webp_filename', el.webp); fd.append('org_ref', el.ref);
        fd.append('plik', z.blob, 'oryginal.jpg');
        return fd;
      },
      wyslij: (fd, naglowki) => fetch('/api/photo-match/przegladarka/podmien', { method: 'POST', headers: naglowki, body: fd }),
    }, poKolei, NARAZ_WYSYLKA, (teraz, razem) => setPostep({ teraz, razem, tekst: 'Wgrywam oryginały…' }), window.LF_ZAM);
    const lista = wyniki.map((w, i) => ({ ...doPodmiany[i], ok: w.ok, akcja: w.ok ? w.wynik.action : null, blad: w.ok ? null : String(w.blad && w.blad.message || w.blad) }));
    setPodmiany(lista); setStage('done');
    const udane = lista.filter(x => x.ok).length;
    if (udane > 0 && onReplaceComplete) onReplaceComplete({ replaced: udane });
    const odmowa = wyniki.find(w => w.cenaZmieniona);
    if (odmowa) {
      const c = odmowa.cenaZmieniona;
      powiedz(`Cena podmiany zmieniła się${c.aktualna === null ? '' : ` (teraz ${c.aktualna} tok)`} — pozostałych zdjęć nie wysłano. Kliknij ponownie, żeby zobaczyć nową kwotę.`, 'danger');
    } else {
      powiedz(udane === lista.length ? `Podmieniono ${udane} zdj.` : `Podmieniono ${udane} z ${lista.length} — reszta z błędem`, udane === lista.length ? 'success' : 'danger');
    }
  }, [doPodmiany, onReplaceComplete, fire]);

  /* ── widok ─────────────────────────────────────────────────────────────────── */
  const liczbaZaznaczonych = doPodmiany.length;
  const etykietaPewnosci = (m) => m.confidence === 'high' ? 'pewne' : m.confidence === 'medium' ? 'możliwe' : 'brak';
  // P4 (nowy wygląd): pewność na tokenach z kontrastem ≥ 4,5:1 w obu motywach; „możliwe” = pomarańcz
  // propozycji Studia (--propozycja, photo-match R13 — nigdy --accent), definicja w galeria3/index.html.
  const kolorPewnosci = (m) => m.confidence === 'high' ? 'var(--green-text)' : m.confidence === 'medium' ? 'var(--propozycja)' : 'var(--text-3)';
  // Wiersz wyniku: na telefonie zawija się (miniatury + pewność w pierwszej linii, „podmień” pod spodem).
  const wiersz = (obramowanie) => ({ display: 'flex', alignItems: 'center', gap: 12, padding: '6px 0', borderTop: obramowanie, ...(telefon ? { flexWrap: 'wrap', gap: 8 } : null) });
  const mini = telefon ? { width: 60, height: 80 } : { width: 72, height: 96 };
  const pewnosc = telefon ? { minWidth: 52 } : { minWidth: 70 };
  const opisWiersza = telefon ? { flex: '1 1 100px', minWidth: 0 } : { flex: 1 };
  const poleWyboru = { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer', ...(dotyk ? { minHeight: 44, padding: '0 8px', fontSize: 14 } : null), ...(telefon ? { marginLeft: 'auto' } : null) };
  const kratka = dotyk ? { width: 22, height: 22 } : undefined;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
         onClick={(e) => { if (e.target === e.currentTarget && stage !== 'hashing' && stage !== 'replacing') zamknij(); }}>
      <div style={{ background: 'var(--panel)', border: '1px solid var(--line-2)', borderRadius: 12, width: 'min(920px, 94vw)', maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: 'var(--shadow)',
        ...(telefon ? { width: '100%', height: '100%', maxHeight: 'none', borderRadius: 0, border: 'none', paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' } : null) }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--line)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', ...(telefon ? { padding: '8px 12px 8px 16px', gap: 8 } : null) }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600 }}>📷 Podmień na oryginały</div>
            <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{sourceIds.length} {sourceIds.length === 1 ? 'ogłoszenie' : 'ogłoszeń'} · zdjęcia z Vinted zastąpimy Twoimi oryginałami z telefonu</div>
          </div>
          <button onClick={zamknij} disabled={stage === 'hashing' || stage === 'replacing'} style={{ ...btn(stage === 'hashing' || stage === 'replacing', false), ...cel }}>Zamknij</button>
        </div>

        <div style={{ padding: 18, overflowY: 'auto', flex: 1, ...(telefon ? { padding: 12, overflowX: 'hidden' } : null) }}>
          {stage === 'pick' && (
            <div>
              <p style={{ fontSize: 13.5, lineHeight: 1.5, color: 'var(--text-2)', marginTop: 0 }}>
                Wskaż folder ze zdjęciami z telefonu (JPG). Na serwer trafią tylko małe miniaturki do porównania,
                a potem wyłącznie te oryginały, które pasują do zdjęć z ogłoszenia — zmniejszone do 2000 px.
                Stare zdjęcia zostają jako kopia zapasowa.
              </p>
              <input ref={inputRef} type="file" webkitdirectory="" directory="" multiple accept="image/jpeg,.jpg,.jpeg" onChange={naFolder} style={{ display: 'none' }} />
              <button onClick={() => inputRef.current && inputRef.current.click()} style={{ ...btn(false), ...cel }}>Wybierz folder ze zdjęciami</button>
              <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 10 }}>Zdjęcia HEIC (iPhone) nie zostaną odczytane — zapisz je jako JPG.</div>
            </div>
          )}

          {(stage === 'hashing' || stage === 'matching' || stage === 'replacing') && (
            <Pasek teraz={postep.teraz} razem={postep.razem} tekst={postep.tekst} />
          )}

          {stage === 'error' && (
            <div>
              <div style={{ color: 'var(--danger-text)', fontSize: 13.5, marginBottom: 12 }}>{blad}</div>
              <button onClick={() => { setBlad(null); setStage('pick'); }} style={{ ...btn(false, false), ...cel }}>Spróbuj z innym folderem</button>
            </div>
          )}

          {stage === 'results' && wynik && (
            <div>
              <div style={{ fontSize: 12.5, color: 'var(--text-2)', marginBottom: 12 }}>
                Przejrzano {wynik.total_org} oryginałów · pewne: <b>{wynik.matched_high}</b> · możliwe: <b>{wynik.matched_medium}</b> · bez pary: <b>{wynik.no_match}</b>
                {(pominiete.nieJpg > 0 || pominiete.nieczytelne > 0) && <span style={{ color: 'var(--text-3)' }}> · pominięto {pominiete.nieJpg + pominiete.nieczytelne} (nie JPG lub nieczytelne)</span>}
              </div>
              {(wynik.groups || []).map(g => (
                <div key={g.source_id} style={{ marginBottom: 16, border: '1px solid var(--line)', borderRadius: 8, padding: 10 }}>
                  <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 8 }}>Ogłoszenie {g.source_id}{g.error && <span style={{ color: 'var(--danger-text)' }}> — {g.error}</span>}</div>
                  {(g.matches || []).map(m => {
                    const key = `${g.source_id}__${m.webp_filename}`;
                    const edyt = (g.edits || {})[m.webp_filename];
                    const maEdycje = !!(edyt && (edyt.bg || edyt.shadow || edyt.vinted_dedupe));
                    const juz = (g.already_matched_keys || []).includes(m.webp_filename);
                    const url = m.matched ? urlOryginalu(m.org_path) : null;
                    return (
                      <div key={key} style={wiersz('1px solid var(--line)')}>
                        <div style={{ ...mini, background: 'var(--pod-podgladem)', color: 'var(--na-zdjeciu-napis)', borderRadius: 6, overflow: 'hidden', flexShrink: 0 }}>
                          <img src={`/api/thumb/${encodeURIComponent(g.source_id)}/${encodeURIComponent(m.webp_filename)}?w=180`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </div>
                        <div style={{ ...pewnosc, textAlign: 'center', fontSize: 11, fontWeight: 600, color: kolorPewnosci(m) }}>→ {etykietaPewnosci(m)}{m.distance != null && <div style={{ fontWeight: 400, color: 'var(--text-3)' }}>odl. {m.distance}</div>}</div>
                        <div style={{ ...mini, background: 'var(--pod-podgladem)', borderRadius: 6, overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--na-zdjeciu-napis)', fontSize: 11 }}>
                          {url ? <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : 'brak'}
                        </div>
                        <div style={{ ...opisWiersza, fontSize: 12 }}>
                          <div style={{ color: 'var(--text-2)' }}>{m.matched ? m.org_filename : 'nie znaleziono oryginału'}</div>
                          {m.matched && m.org_width && <div style={{ color: 'var(--text-3)', fontSize: 11 }}>{m.org_width}×{m.org_height} px</div>}
                          {juz && <div style={{ color: 'var(--text-3)', fontSize: 11 }}>już wcześniej podmienione</div>}
                          {maEdycje && <div style={{ color: 'var(--warn-text)', fontSize: 11 }}>to zdjęcie ma nałożone tło lub cień — podmiana je cofnie</div>}
                        </div>
                        {m.matched && (
                          <label style={poleWyboru}>
                            <input type="checkbox" checked={!!decyzje[key]} onChange={e => setDecyzje(d => ({ ...d, [key]: e.target.checked }))} style={kratka} /> podmień
                          </label>
                        )}
                      </div>
                    );
                  })}
                  {(g.sequence_proposals || []).map(p => {
                    const key = `${g.source_id}__${p.webp_filename}`;
                    const url = urlOryginalu(p.suggested_org_path);
                    return (
                      <div key={key} style={wiersz('1px dashed var(--line)')}>
                        <div style={{ ...mini, background: 'var(--pod-podgladem)', color: 'var(--na-zdjeciu-napis)', borderRadius: 6, overflow: 'hidden', flexShrink: 0 }}>
                          <img src={`/api/thumb/${encodeURIComponent(g.source_id)}/${encodeURIComponent(p.webp_filename)}?w=180`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </div>
                        <div style={{ ...pewnosc, textAlign: 'center', fontSize: 11, fontWeight: 600, color: 'var(--text-3)' }}>→ propozycja</div>
                        <div style={{ ...mini, background: 'var(--pod-podgladem)', color: 'var(--na-zdjeciu-napis)', borderRadius: 6, overflow: 'hidden', flexShrink: 0 }}>
                          {url && <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                        </div>
                        <div style={{ ...opisWiersza, fontSize: 12 }}>
                          <div style={{ color: 'var(--text-2)' }}>{p.suggested_org_filename}</div>
                          <div style={{ color: 'var(--text-3)', fontSize: 11 }}>sąsiednie zdjęcie z tej samej serii — sprawdź, czy to ono</div>
                        </div>
                        <label style={poleWyboru}>
                          <input type="checkbox" checked={!!decyzje[key]} onChange={e => setDecyzje(d => ({ ...d, [key]: e.target.checked }))} style={kratka} /> podmień
                        </label>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}

          {stage === 'done' && (
            <div>
              <div style={{ fontSize: 13.5, marginBottom: 10 }}>Gotowe: podmieniono <b>{podmiany.filter(p => p.ok).length}</b> z {podmiany.length}.</div>
              {podmiany.filter(p => !p.ok).map(p => (
                <div key={`${p.sid}__${p.webp}`} style={{ fontSize: 12, color: 'var(--danger-text)' }}>{p.sid} / {p.webp}: {p.blad}</div>
              ))}
              <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 10 }}>Poprzednie wersje zdjęć zostały zachowane jako kopia zapasowa.</div>
            </div>
          )}
        </div>

        {stage === 'results' && (
          <div style={{ padding: '12px 18px', borderTop: '1px solid var(--line)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button onClick={() => { setWynik(null); setStage('pick'); }} style={{ ...btn(false, false), ...cel }}>Inny folder</button>
            <button onClick={podmien} disabled={liczbaZaznaczonych === 0} style={{ ...btn(liczbaZaznaczonych === 0), ...cel }}>Podmień zaznaczone ({liczbaZaznaczonych})</button>
          </div>
        )}
        {stage === 'done' && (
          <div style={{ padding: '12px 18px', borderTop: '1px solid var(--line)', display: 'flex', justifyContent: 'flex-end' }}>
            <button onClick={zamknij} style={{ ...btn(false), ...cel }}>Zamknij</button>
          </div>
        )}
      </div>
    </div>
  );
}
